// `SlideOutPanel` is the legacy eager slide-out primitive — the side-anchored
// sibling of `FullScreenModal` (`./FullScreenModal.tsx`). Both were split out
// of the original eager modal, which BL-1710 retired onto `Modal`.
//
// Unlike `Modal`, it renders its `{children}` subtree unconditionally
// by default: the open/close transition is a `translate-x` slide (`translate-y`
// for `side="bottom"`) plus
// pointer-events, and every hook inside the panel runs even while it is closed
// (the BUG-523 class of defect). `lazy` (BL-1439) is the opt-in escape from
// that — see the prop's own docblock for why it gates the subtree and not the
// shell.
//
// Focus management is delegated to `useFocusTrap` (`@auctogon/ui/web`) — the
// shared core per ADR 030 — Design Canon, Decision 2. This primitive carried a
// byte-near copy of the same effect until BUG-1386, whose guessed 50ms timer
// overrode a field the operator had already clicked into. The copies are gone,
// not deprecated (ADR 044 — Must Consolidate).

'use client';

import { useEffect, useState, useRef, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import {
    isNonModalDismissTarget,
    useBackdropDismiss,
    useBodyScrollLock,
    useFocusOnOpen,
    useFocusTrap,
    useOutsideClick,
    useTopmostEscape,
    useVisualViewportBox,
} from '@auctogon/ui/web';

import { OverlayLayerProvider, closedOverlayA11yProps, useOverlayZIndex } from './overlay-layer';

/**
 * Props for the SlideOutPanel component
 */
export interface SlideOutPanelProps {
    /** Whether the panel is open */
    isOpen: boolean;
    /** Callback when the panel should close */
    onClose: () => void;
    /** Panel content */
    children: React.ReactNode;
    /** Accessible label for the panel */
    ariaLabel?: string;
    /**
     * Width of the panel (default: 'max-w-3xl').
     *
     * Ignored for `side="bottom"` — a sheet spans the full width, and a
     * `max-w-*` there would cap it AND pin the result to the left edge.
     */
    width?: string;
    /**
     * An exact CSS max-width that overrides `width` when set — for a panel that
     * must match a live sibling column rather than a fixed step (BUG-1883: the
     * clerk trays cover the bidder rail, whose width is a container-query
     * ladder). `null`/omitted leaves `width` in charge. Ignored for `side="bottom"`.
     */
    maxWidth?: string | null;
    /**
     * Which edge the panel slides from (default: 'right').
     *
     * `'bottom'` is the phone-tier sheet: full-bleed, capped at 85dvh, rounded
     * top corners, grab handle. It carries the bidder app's refined timing —
     * `ease-out` on the panel and a 200ms scrim — rather than the side panels'
     * `ease-in-out` / 300ms, because a sheet travels a short distance and an
     * ease-IN start reads as lag under a thumb. The side values keep their
     * existing timing byte-for-byte, so the current callers (including the
     * dashboard's `side="left"` nav drawer) don't shift under them.
     */
    side?: 'left' | 'right' | 'bottom';
    /**
     * BL-2746: a `side="bottom"` sheet's height. `'fit'` (default) sizes to its
     * content, capped at 85dvh. `'tall'` stands 48px short of the top of the
     * screen, so the page still reads as underneath (the customer picker's
     * long browse list). Ignored for the side panels.
     */
    bottomHeight?: 'fit' | 'tall';
    /**
     * Defer the `{children}` subtree until the panel is FIRST opened. Default
     * `false`, so every existing consumer keeps its eager subtree. (Dropping the
     * closed panel out of the accessibility tree is no longer lazy-only: every
     * closed panel is `inert` + `aria-hidden` since BL-1959.)
     *
     * "First opened", not "while open": once mounted the subtree STAYS mounted,
     * because unmounting on close races the slide-out transition — see the
     * `hasOpened` comment in the body.
     *
     * This is the BUG-523 property for slide-outs (BL-1439). It gates the
     * SUBTREE, not the shell, and that distinction is the design:
     *
     *  - Unmounting the whole portal while closed would mount the panel already
     *    at `translate-x-0` on open, so it would appear instantly instead of
     *    sliding. Keeping the shell preserves the transition and `data-state`.
     *  - Gating the children is what actually buys the property worth having:
     *    the hooks, fetches and context reads inside a closed panel never run.
     *    BL-1439's child is the entire `Sidebar`, so eager-mounting it would put
     *    a second nav tree on every dashboard page at every viewport.
     *  - A shell with no children is an empty `role="dialog"`, which assistive
     *    tech would still announce and Tab could still enter — hence the
     *    `aria-hidden` + `inert` pair on the closed panel, which BL-1959 later
     *    extended to the eager path too. `[data-testid][data-state="open"]`
     *    queries (`automation-handles.md`) are unaffected by it.
     */
    lazy?: boolean;
    /** Optional custom class for the backdrop */
    backdropClassName?: string;
    /** Optional custom class for the panel */
    panelClassName?: string;
    /** Whether clicking the backdrop closes the panel (default: true) */
    closeOnBackdropClick?: boolean;
    /**
     * Modal (default `true`): a scrim, a body scroll lock, a focus trap and
     * `aria-modal`. Every existing slide-out keeps that.
     *
     * `false` is a panel the page stays usable BESIDE (BL-2087, the clerk's
     * message tray over the pinned rail): no scrim, no scroll lock, no trap —
     * focus still moves in on open. A pointer-down on EMPTY page area closes it
     * (`isNonModalDismissTarget`); one on a control behind it lets the control
     * act and leaves the panel open. Escape still closes it, and a click that
     * belongs to an overlay stacked above it never does.
     */
    modal?: boolean;
    /**
     * Z-index for the panel. Omit it and the panel layers itself relative to
     * any overlay it was opened from (see `./overlay-layer.tsx`) — 50 at the
     * page root, one step above an enclosing modal or slide-out. Pass a value
     * only to pin an exact layer.
     */
    zIndex?: number;
    /**
     * Test-ID namespace for automated checks. No default: a page can render
     * several, and a shared default would tag them all identically
     * (`.claude/rules/automation-handles.md`).
     * `data-state` is emitted regardless.
     *
     * Pair it with the `data-state` attribute this renders, NOT with presence:
     * this primitive mounts its subtree unconditionally and "closes" via
     * transform/opacity alone, so a closed dialog is still fully in the DOM
     * (though `inert` + `aria-hidden`, so out of the accessibility tree —
     * BL-1959). `[data-testid="x"][data-state="open"]` is the only correct
     * query -- a bare presence check matches a closed dialog.
     */
    testId?: string;
}

/**
 * The open panel's behaviour, modal or not: topmost-only Escape, and either the
 * modal trio (scroll lock, focus trap) or the non-modal pair (focus-on-open,
 * empty-area click-out — BL-2087). Split out of `SlideOutPanel` to keep the
 * component under the 100-line limit; it owns no state of its own.
 *
 * @sideEffect Registers document keydown / mousedown listeners and the body scroll lock while open.
 */
function usePanelBehaviour({
    panelRef,
    isOpen,
    mounted,
    modal,
    onClose,
}: {
    panelRef: RefObject<HTMLDivElement | null>;
    isOpen: boolean;
    mounted: boolean;
    modal: boolean;
    onClose: () => void;
}): void {
    // Escape closes only the TOPMOST overlay (BUG-1399). The legacy eager
    // primitives each registered a bare `document` listener, so one keypress closed
    // the whole stack — a `Modal` opened from inside a `SlideOutPanel` took the
    // panel down with it. Same shared-hook consolidation as `useFocusTrap`.
    const isTopmost = useTopmostEscape(isOpen, onClose);

    // The shared iOS-safe, ref-counted lock (BUG-1849): `overflow: hidden` alone
    // let iOS pan the page behind, and per-overlay save/restore unwound stacked
    // overlays out of order. A non-modal panel takes none (BL-2087 AC 1).
    useBodyScrollLock(isOpen && modal);

    useFocusTrap(panelRef, isOpen && mounted && modal);
    useFocusOnOpen(panelRef, isOpen && mounted && !modal);

    // BL-2087 — a non-modal panel's click-out. Ignored unless the click landed on
    // empty page area AND this panel is the top overlay: a click on a stacked
    // overlay's scrim belongs to that overlay.
    useOutsideClick([panelRef], onClose, {
        enabled: isOpen && !modal,
        ignore: (target, event) =>
            !isTopmost() || !isNonModalDismissTarget(target, { offsetX: event.offsetX, offsetY: event.offsetY }),
    });
}

/** A bottom sheet's height cap, as a share of the visible area (the class's `85dvh`). */
const SHEET_HEIGHT_SHARE = 0.85;

/** BL-2746: the gap a `tall` bottom sheet leaves above itself (the prototype's `calc(100% - 48px)`). */
const TALL_SHEET_TOP_GAP_PX = 48;

/**
 * BL-2214 — a bottom sheet's offsets while the on-screen keyboard is up. iOS
 * never shrinks the layout viewport for the keyboard and `dvh` does not track
 * it, so a `bottom-0` sheet with a focused field keeps its lower half behind
 * the keys. The visual viewport is the only keyboard-aware size (BUG-1840, the
 * same fix Modal's phone sheet carries): lift the floor by the keyboard and cap
 * the height to the visible area.
 *
 * @param enabled - only an open `side="bottom"` panel follows the viewport.
 * @returns the inline style to merge, or `undefined` with no keyboard up (the
 *   classes' `bottom-0` + `max-h-[85dvh]` then stand).
 */
function useSheetKeyboardOffset(enabled: boolean, tall: boolean): { bottom: string; maxHeight: string } | undefined {
    const box = useVisualViewportBox(enabled);
    if (box == null || typeof window === 'undefined') return undefined;
    const keyboard = Math.round(window.innerHeight - (box.top + box.height));
    if (keyboard < 1) return undefined;
    const maxHeight = tall ? box.height - TALL_SHEET_TOP_GAP_PX : box.height * SHEET_HEIGHT_SHARE;
    return { bottom: `${keyboard}px`, maxHeight: `${Math.round(maxHeight)}px` };
}

/**
 * Slide-out panel component that renders in a portal.
 * Includes focus trapping and focus restoration for accessibility.
 *
 * Shares the legacy eager-render pattern with `FullScreenModal`
 * (`./FullScreenModal.tsx`) by default — children mount on first render and
 * stay mounted; the slide animation is the only open/close affordance. Pass
 * `lazy` to defer the children subtree until first open (BL-1439); the shell,
 * and therefore the transition, is unaffected.
 */
export function SlideOutPanel({
    isOpen,
    onClose,
    testId,
    children,
    ariaLabel,
    width = 'max-w-3xl',
    maxWidth = null,
    side = 'right',
    bottomHeight = 'fit',
    lazy = false,
    backdropClassName,
    panelClassName,
    closeOnBackdropClick = true,
    modal = true,
    zIndex: explicitZIndex,
}: SlideOutPanelProps) {
    const zIndex = useOverlayZIndex(explicitZIndex);
    const isBottom = side === 'bottom';
    const [mounted, setMounted] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);

    // `lazy` defers the subtree until the FIRST open, then keeps it — it does not
    // unmount on close, and that distinction is load-bearing rather than lazy
    // bookkeeping. Unmounting on close tears the children out in the same render
    // that starts the 300ms `translate-x` slide, so what actually slides off
    // screen is this panel's own empty `bg-gray-50` box — a light rectangle where
    // the operator's content just was, on every close.
    //
    // Deferring the unmount behind a `transitionend` would fix the flash and add
    // a timing dependency on a CSS duration; keeping the subtree is simpler and
    // costs nothing the eager path wasn't already paying. The property worth
    // having is "never mounts on a page where the operator doesn't open it" —
    // which is every dashboard page for every desktop operator — and that is
    // fully preserved. Derived state during render, not an effect
    // (`react-hooks/set-state-in-effect`).
    const [hasOpened, setHasOpened] = useState(isOpen);
    if (isOpen && !hasOpened) setHasOpened(true);

    useEffect(() => {
        setMounted(true);
    }, []);

    usePanelBehaviour({ panelRef, isOpen, mounted, modal, onClose });
    const keyboardOffset = useSheetKeyboardOffset(isOpen && isBottom, bottomHeight === 'tall');

    // The scrim is a sibling of the panel, so a drag from the panel never lands a
    // click on it; the shared guard (BUG-2308) keeps every overlay on one rule.
    const backdropDismiss = useBackdropDismiss({ enabled: closeOnBackdropClick, onDismiss: onClose });

    if (!mounted) return null;

    const panelContent = (
        <>
            {/* Backdrop — modal panels only (BL-2087). */}
            {modal ? (
                <div
                    className={clsx(
                        'fixed inset-0 bg-black/50 transition-opacity',
                        // The sheet's scrim settles ahead of the sheet itself (200
                        // vs 300ms) so the page behind darkens first and the sheet
                        // arrives onto an already-dimmed surface, rather than the
                        // two racing to the same finish line. Bidder-web's
                        // `BottomSheet` is where this pairing comes from.
                        isBottom ? 'duration-200' : 'duration-300',
                        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
                        backdropClassName
                    )}
                    style={{ zIndex: zIndex - 1 }}
                    {...backdropDismiss}
                    aria-hidden="true"
                    // Reduce Motion strips this fade (@auctogon/ui/styles/reduced-motion.css, BUG-1557).
                    data-overlay-motion=""
                    // Derived from the panel's own handle rather than a second prop
                    // (`automation-handles.md` — namespace off an id the component
                    // already has). On a touch surface the scrim is the primary
                    // dismissal, so an automated check needs to be able to click it.
                    data-testid={testId ? `${testId}-backdrop` : undefined}
                />
            ) : null}
            {/* Panel */}
            <div
                ref={panelRef}
                className={clsx(
                    'fixed shadow-2xl transform transition-transform duration-300 overflow-hidden flex flex-col',
                    isBottom
                        ? [
                              // BL-2545 (Vadim, 2026-10-20): a bottom sheet is white from its
                              // grab-handle strip down, so the strip above a white header no
                              // longer reads as a grey band. Every bottom sheet, not just pickers.
                              'bg-white',
                              // `dvh`, NOT `vh`. On a phone `vh` resolves to the
                              // tallest the viewport ever gets, so an `85vh`
                              // sheet sits partly behind the browser's own
                              // chrome and its last row is unreachable — the
                              // exact defect this session already fixed once.
                              'inset-x-0 bottom-0 rounded-t-2xl ease-out',
                              // BL-2746: a tall sheet stops 48px short of the top.
                              bottomHeight === 'tall' ? 'h-[calc(100dvh-48px)]' : 'max-h-[85dvh]',
                              // The app sets `viewport-fit=cover`, so on a
                              // notched device the sheet's own floor is under
                              // the home indicator without this.
                              'pb-[env(safe-area-inset-bottom)]',
                              isOpen ? 'translate-y-0' : 'translate-y-full',
                          ]
                        : [
                              'bg-gray-50 top-0 h-full w-full ease-in-out',
                              width,
                              side === 'right' ? 'right-0' : 'left-0',
                              side === 'right'
                                  ? (isOpen ? 'translate-x-0' : 'translate-x-full')
                                  : (isOpen ? 'translate-x-0' : '-translate-x-full'),
                          ],
                    panelClassName
                )}
                style={isBottom ? { zIndex, ...keyboardOffset } : maxWidth == null ? { zIndex } : { zIndex, maxWidth }}
                // Reduce Motion strips this slide (reduced-motion.css, BUG-1557).
                data-overlay-motion=""
                // -1, not 0: the container must be programmatically focusable
                // so the trap can hand focus to it when the overlay opens with
                // nothing focusable inside (a loading shell), without itself
                // joining the tab order. BUG-1386.
                tabIndex={-1}
                role="dialog"
                aria-modal={modal}
                aria-label={ariaLabel}
                data-testid={testId}
                data-state={isOpen ? 'open' : 'closed'}
                // Closed: inert + aria-hidden on BOTH paths. The lazy path had
                // this first (BL-1439, for its empty shell); BL-1959 extended it
                // to the eager path, whose closed subtree was a reachable ghost.
                {...closedOverlayA11yProps(isOpen)}
            >
                {isBottom && (
                    // Grab handle. The sheet has no header of its own (callers
                    // bring their own content), so this is the only affordance
                    // saying "this dismisses downward". Decorative on purpose:
                    // the scrim and Escape are the real dismissals, so it takes
                    // no pointer handler and is hidden from assistive tech.
                    <div className="flex shrink-0 justify-center pt-3 pb-1" aria-hidden="true">
                        <span className="h-1 w-9 rounded-full bg-gray-300" />
                    </div>
                )}
                {(!lazy || hasOpened) && (
                    <OverlayLayerProvider zIndex={zIndex}>{children}</OverlayLayerProvider>
                )}
            </div>
        </>
    );

    return createPortal(panelContent, document.body);
}
